import { inject, Injectable, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs/internal/Subject';

import { VIDEO_FEED_TYPE } from '@/shared/constants/video-feed.constant';
import { Clip } from '@/shared/models/clip.model';
import { isScoutingFeedClip } from '@/shared/utils/clip-feed.util';

export interface VideoModalData {
  clip: Clip | Clip[] | object;
  autoplay: boolean;
  type?: string;
  index?: number;
  clipType?: string[];
  clipTypeCustomSeconds?: { before: number; after: number };
  phaseFilterIds?: number[];
  opponentPhaseFilterIds?: number[];
  outcomeFilterIds?: string[];
}
export interface SoccerFieldModalData {
  clip: Clip;
  clips?: Clip[];
  startIndex?: number;
  showFormationArrow: string;
  itemAction: string;
  showToolbar: string;
  playerDetailModal: string;
}

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  currentClipChanged = new Subject<Clip>();

  readonly isLoadingWideAngleVideo = signal(false);

  private dialog = inject(MatDialog);

  // The video player and the 2D field are mutually exclusive: only one player modal may be open
  // at a time. Opening either one closes the currently open player so they never stack/duplicate.
  private activePlayerDialog: MatDialogRef<unknown> | null = null;

  async openVideoModal(data: VideoModalData): Promise<MatDialogRef<unknown>> {
    this.closeActivePlayerDialog();
    const isWideAngle = data.type === VIDEO_FEED_TYPE.SCOUTING || this.hasScoutingClip(data.clip);
    if (isWideAngle) {
      this.isLoadingWideAngleVideo.set(true);
    }
    const { VideoComponent } = await import('../components/video/video.component');
    const ref = this.trackPlayerDialog(
      this.dialog.open(VideoComponent, {
        data: data,
      })
    );
    if (isWideAngle) {
      ref.afterClosed().subscribe(() => this.isLoadingWideAngleVideo.set(false));
    }
    return ref;
  }

  async openSoccerFieldModal(data: SoccerFieldModalData): Promise<MatDialogRef<unknown>> {
    this.closeActivePlayerDialog();
    const { SoccerFieldVideoComponent } = await import('../components/soccer-field-video/soccer-field-video.component');
    return this.trackPlayerDialog(
      this.dialog.open(SoccerFieldVideoComponent, {
        data: data,
        panelClass: 'soccer-field-video-panel',
        maxWidth: '95vw',
        maxHeight: '95vh',
      })
    );
  }

  private hasScoutingClip(clip: VideoModalData['clip']): boolean {
    if (Array.isArray(clip)) {
      return clip.some((item: Clip) => isScoutingFeedClip(item));
    }
    return isScoutingFeedClip(clip as Clip);
  }

  private trackPlayerDialog(ref: MatDialogRef<unknown>): MatDialogRef<unknown> {
    this.activePlayerDialog = ref;
    ref.afterClosed().subscribe(() => {
      if (this.activePlayerDialog === ref) {
        this.activePlayerDialog = null;
      }
    });
    return ref;
  }

  private closeActivePlayerDialog(): void {
    this.activePlayerDialog?.close();
    this.activePlayerDialog = null;
  }
}
